import { useState, useEffect } from 'react'
import { User, Lock, Loader2, Save, DollarSign, Wallet } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../store/authStore'
import api from '../services/api'
import { fetchPayRate, updatePayRate } from '../services/adminService'
import { passwordRuleProblem, PASSWORD_RULE_HINT } from '../services/passwordRule'

const PLAN_COLORS: Record<string, string> = {
  BASIC: 'border-gray-300 bg-gray-50',
  STANDARD: 'border-blue-300 bg-blue-50',
  PREMIUM: 'border-amber-300 bg-amber-50',
}

const PLAN_LABELS: Record<string, string> = {
  BASIC: 'Basic Plan',
  STANDARD: 'Standard Plan',
  PREMIUM: 'Premium Plan',
}

const PLAN_BADGES: Record<string, { text: string; color: string } | null> = {
  BASIC: null,
  STANDARD: { text: 'RECOMMENDED', color: 'bg-blue-500' },
  PREMIUM: { text: 'BEST VALUE', color: 'bg-amber-500' },
}

type PlanConfig = { price: string; pickups: string; discount: string }

export default function Settings() {
  const user = useAuthStore((s) => s.user)
  const setSessionTokens = useAuthStore((s) => s.setSessionTokens)
  // Pricing and pay rate are platform-wide, super-admin-only endpoints. A company
  // operator (or a zone admin) reaching this page got two cards full of defaults
  // and Save buttons that always failed.
  const canEditPlatformSettings = user?.role === 'ADMIN' && user?.isSuperAdmin === true

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [saving, setSaving] = useState(false)

  // Collector pay rate
  const [payRateLoading, setPayRateLoading] = useState(true)
  const [payRateSaving, setPayRateSaving] = useState(false)
  const [payRate, setPayRate] = useState('')

  useEffect(() => {
    if (!canEditPlatformSettings) { setPayRateLoading(false); return }
    fetchPayRate()
      .then((data) => {
        if (data) setPayRate(String(data.payRate ?? ''))
      })
      .catch(() => toast.error('Could not load the collector pay rate'))
      .finally(() => setPayRateLoading(false))
  }, [canEditPlatformSettings])

  const handleSavePayRate = async () => {
    const rate = parseFloat(payRate)
    if (isNaN(rate) || rate <= 0) { toast.error('Pay rate must be a positive number'); return }
    setPayRateSaving(true)
    try {
      await updatePayRate(rate)
      toast.success('Collector pay rate updated')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to update pay rate')
    } finally {
      setPayRateSaving(false)
    }
  }

  // Pricing config
  const [pricingLoading, setPricingLoading] = useState(true)
  const [pricingSaving, setPricingSaving] = useState(false)
  const [pickupPrice, setPickupPrice] = useState('49')
  const [defaultBinSize, setDefaultBinSize] = useState('240')

  // 3-tier plan configs
  const [plans, setPlans] = useState<Record<string, PlanConfig>>({
    BASIC: { price: '37', pickups: '1', discount: '10' },
    STANDARD: { price: '62', pickups: '2', discount: '15' },
    PREMIUM: { price: '75', pickups: '3', discount: '20' },
  })

  useEffect(() => {
    if (!canEditPlatformSettings) { setPricingLoading(false); return }
    api.get('/admin/settings/pricing').then((res) => {
      const data = res.data?.data ?? res.data
      if (data) {
        setPickupPrice(String(data.pickupPrice ?? '49'))
        setDefaultBinSize(String(data.defaultBinSize ?? '240'))

        // Load per-plan configs if available
        if (data.plans) {
          const p = data.plans
          setPlans({
            BASIC: {
              price: String(p.BASIC?.price ?? '37'),
              pickups: String(p.BASIC?.pickupsIncluded ?? '1'),
              discount: String(Math.round((p.BASIC?.extraPickupDiscount ?? 0.10) * 100)),
            },
            STANDARD: {
              price: String(p.STANDARD?.price ?? '62'),
              pickups: String(p.STANDARD?.pickupsIncluded ?? '2'),
              discount: String(Math.round((p.STANDARD?.extraPickupDiscount ?? 0.15) * 100)),
            },
            PREMIUM: {
              price: String(p.PREMIUM?.price ?? '75'),
              pickups: String(p.PREMIUM?.pickupsIncluded ?? '3'),
              discount: String(Math.round((p.PREMIUM?.extraPickupDiscount ?? 0.20) * 100)),
            },
          })
        }
      }
    }).catch(() => toast.error('Could not load pricing settings')).finally(() => setPricingLoading(false))
  }, [canEditPlatformSettings])

  const updatePlan = (plan: string, field: keyof PlanConfig, value: string) => {
    setPlans((prev) => ({ ...prev, [plan]: { ...prev[plan], [field]: value } }))
  }

  const handleSavePricing = async () => {
    const price = parseFloat(pickupPrice)
    const binSize = parseInt(defaultBinSize, 10)
    if (isNaN(price) || price <= 0) { toast.error('Invalid pickup price'); return }
    if (isNaN(binSize) || binSize <= 0) { toast.error('Invalid bin size'); return }

    // Validate each plan
    for (const [key, cfg] of Object.entries(plans)) {
      const p = parseFloat(cfg.price)
      const pk = parseInt(cfg.pickups, 10)
      const d = parseFloat(cfg.discount)
      if (isNaN(p) || p <= 0) { toast.error(`${PLAN_LABELS[key]}: Invalid price`); return }
      if (isNaN(pk) || pk < 1 || pk > 20) { toast.error(`${PLAN_LABELS[key]}: Pickups must be 1-20`); return }
      if (isNaN(d) || d < 0 || d > 100) { toast.error(`${PLAN_LABELS[key]}: Discount must be 0-100%`); return }
    }

    setPricingSaving(true)
    try {
      await api.patch('/admin/settings/pricing', {
        pickupPrice: price,
        defaultBinSize: binSize,
        // Per-plan settings
        basicPrice: parseFloat(plans.BASIC.price),
        basicPickups: parseInt(plans.BASIC.pickups, 10),
        basicDiscount: parseFloat(plans.BASIC.discount) / 100,
        standardPrice: parseFloat(plans.STANDARD.price),
        standardPickups: parseInt(plans.STANDARD.pickups, 10),
        standardDiscount: parseFloat(plans.STANDARD.discount) / 100,
        premiumPrice: parseFloat(plans.PREMIUM.price),
        premiumPickups: parseInt(plans.PREMIUM.pickups, 10),
        premiumDiscount: parseFloat(plans.PREMIUM.discount) / 100,
      })
      toast.success('Pricing updated. New bookings and plans use these prices from now on.')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to update pricing')
    } finally {
      setPricingSaving(false)
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      toast.error('Please fill in all password fields')
      return
    }
    const problem = passwordRuleProblem(newPassword)
    if (problem) {
      toast.error(problem)
      return
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match')
      return
    }

    setSaving(true)
    try {
      // rotateSession says this panel stores the fresh pair returned below, so
      // the server may end this session with the others. A body field, not a
      // header: a custom header failed CORS on a backend that did not list it
      // yet, which took the whole panel down during a deploy.
      const res = await api.post('/auth/change-password', { currentPassword, newPassword, rotateSession: true })
      // The server ends every session from before the change, this one
      // included, and answers with a fresh pair. Keep it, or the next request
      // would sign this admin out. An older server sends none: keep the
      // current pair.
      const fresh = res.data?.data ?? {}
      if (fresh.token) setSessionTokens(fresh.token, fresh.refreshToken)
      toast.success(res.data?.message ?? 'Password changed')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to change password'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const inputCls = "w-full bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A2E] outline-none focus:border-primary-400"

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Profile Info */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-[#1A1A2E]">Profile Information</h3>
        </div>
        <div className="space-y-3">
          {[
            { label: 'Name', value: user?.name ?? '—' },
            { label: 'Email', value: user?.email ?? '—' },
            { label: 'Role', value: user?.role ?? '—' },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
              <span className="text-xs font-medium text-[#6B7280]">{label}</span>
              <span className="text-sm font-semibold text-[#1A1A2E]">{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Pricing Configuration */}
      {canEditPlatformSettings && (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-[#1A1A2E]">Pricing Configuration</h3>
        </div>
        {pricingLoading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary-600" /></div>
        ) : (
          <div className="space-y-4">
            {/* Global settings */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="pickupPrice" className="text-xs font-medium text-[#6B7280] mb-1 block">Pay-Per-Pickup Price (GHS)</label>
                <input id="pickupPrice" type="number" value={pickupPrice} onChange={(e) => setPickupPrice(e.target.value)} min="0" step="0.01" placeholder="49" className={inputCls} />
              </div>
              <div>
                <label htmlFor="defaultBinSize" className="text-xs font-medium text-[#6B7280] mb-1 block">Default Bin Size (L)</label>
                <input id="defaultBinSize" type="number" value={defaultBinSize} onChange={(e) => setDefaultBinSize(e.target.value)} min="0" placeholder="240" className={inputCls} />
              </div>
            </div>

            {/* 3-Tier Subscription Plans */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-[10px] font-semibold text-[#6B7280] uppercase tracking-wide mb-3">Subscription Plans</p>
            </div>

            <div className="space-y-3">
              {(['BASIC', 'STANDARD', 'PREMIUM'] as const).map((planKey) => {
                const badge = PLAN_BADGES[planKey]
                return (
                  <div key={planKey} className={`rounded-xl border-2 p-4 ${PLAN_COLORS[planKey]} relative`}>
                    {badge && (
                      <span className={`absolute -top-2.5 right-4 text-[9px] font-bold text-white px-2 py-0.5 rounded-full ${badge.color}`}>
                        {badge.text}
                      </span>
                    )}
                    <p className="text-sm font-bold text-[#1A1A2E] mb-3">{PLAN_LABELS[planKey]}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label htmlFor={`${planKey}-price`} className="text-[10px] font-medium text-[#6B7280] mb-1 block">Price (GHS/month)</label>
                        <input id={`${planKey}-price`} type="number" value={plans[planKey].price} onChange={(e) => updatePlan(planKey, 'price', e.target.value)} min="0" step="0.01" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor={`${planKey}-pickups`} className="text-[10px] font-medium text-[#6B7280] mb-1 block">Pickups Included</label>
                        <input id={`${planKey}-pickups`} type="number" value={plans[planKey].pickups} onChange={(e) => updatePlan(planKey, 'pickups', e.target.value)} min="1" max="20" className={inputCls} />
                      </div>
                      <div>
                        <label htmlFor={`${planKey}-discount`} className="text-[10px] font-medium text-[#6B7280] mb-1 block">Extra Pickup Discount (%)</label>
                        <input id={`${planKey}-discount`} type="number" value={plans[planKey].discount} onChange={(e) => updatePlan(planKey, 'discount', e.target.value)} min="0" max="100" className={inputCls} />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            <button
              type="button"
              onClick={handleSavePricing}
              disabled={pricingSaving}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-5 py-2.5 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {pricingSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save All Pricing
            </button>
          </div>
        )}
      </div>

      )}

      {/* Collector Pay Rate */}
      {canEditPlatformSettings && (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-[#1A1A2E]">Collector Pay Rate</h3>
        </div>
        <p className="text-xs text-[#6B7280] mb-4">Amount paid to collectors per completed pickup</p>
        {payRateLoading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-primary-600" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <label htmlFor="payRate" className="text-xs font-medium text-[#6B7280] mb-1 block">Pay Rate (GHS)</label>
              <input id="payRate" type="number" value={payRate} onChange={(e) => setPayRate(e.target.value)} min="0" step="0.01" placeholder="5.00" className={inputCls} />
            </div>
            <button
              type="button"
              onClick={handleSavePayRate}
              disabled={payRateSaving}
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-5 py-2.5 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {payRateSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              Save Pay Rate
            </button>
          </div>
        )}
      </div>

      )}

      {/* Change Password */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="w-5 h-5 text-primary-600" />
          <h3 className="font-semibold text-[#1A1A2E]">Change Password</h3>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[#6B7280] mb-1 block">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder="Enter current password" className={`${inputCls} placeholder-[#6B7280]`} />
          </div>
          <div>
            <label className="text-xs font-medium text-[#6B7280] mb-1 block">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Enter new password" className={`${inputCls} placeholder-[#6B7280]`} />
            <p className="text-[11px] text-[#6B7280] mt-1">{PASSWORD_RULE_HINT}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-[#6B7280] mb-1 block">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" className={`${inputCls} placeholder-[#6B7280]`} />
          </div>
          <button
            type="button"
            onClick={handleChangePassword}
            disabled={saving}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-5 py-2.5 rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Update Password
          </button>
        </div>
      </div>
    </div>
  )
}
